import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import en from "@/locales/en";
import es from "@/locales/es";

const locales = { en, es };
const STORAGE_KEY = "kelo_lang";

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};

const detectInitial = () => {
  if (typeof window === "undefined") return "en";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "es") return saved;
  const nav = (navigator.language || "en").toLowerCase();
  return nav.startsWith("es") ? "es" : "en";
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitial);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Aplica la clase global .lang-changing durante la ventana de salida (~180ms)
  // para que los textos hagan fade-out antes del swap. Se limpia después con
  // doble RAF para asegurar reflow tras el cambio del árbol.
  const setLang = useCallback((l) => {
    if (l !== "en" && l !== "es") return;
    if (l === lang) return;
    setChanging(true);
    document.documentElement.classList.add("lang-changing");
    window.setTimeout(() => {
      window.localStorage.setItem(STORAGE_KEY, l);
      setLangState(l);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.documentElement.classList.remove("lang-changing");
          setChanging(false);
        });
      });
    }, 180);
  }, [lang]);

  const toggle = useCallback(() => setLang(lang === "en" ? "es" : "en"), [lang, setLang]);

  const value = useMemo(
    () => ({ lang, setLang, toggle, changing, t: locales[lang] }),
    [lang, setLang, toggle, changing]
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
